"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DropdownMenu = DropdownMenu;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_dom_1 = require("react-dom");
const cn_1 = require("../lib/cn");
const overlay_1 = require("../lib/overlay");
const button_1 = require("./button");
const field_1 = require("./field");
const icon_1 = require("./icon");
const tooltip_1 = require("./tooltip");
/** Indices of the items a keyboard can land on — not separators, labels or disabled rows. */
function selectableIndices(items) {
    return items.reduce((out, item, index) => {
        if (item.type === undefined && item.disabled !== true)
            out.push(index);
        return out;
    }, []);
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Every row menu, overflow action and picker in the app.                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Portalled, and it has to be.** A menu rendered inside a table row is clipped by the
 * row's `overflow` and stacked under the sticky header. Portalling to `<body>` costs the
 * position maths in `anchorTo` and buys a menu that is never half-visible.
 *
 * ⚠️ **Not a focus trap — `useDismiss`, so Tab LEAVES.** A menu is a shortcut, and trapping
 * focus in one makes the rest of the page unreachable until it is dismissed. Escape, an outside
 * pointer-down and Tab all close it.
 *
 * ⚠️ **Keyboard navigation skips separators, labels and disabled rows.** `selectableIndices` is
 * computed over the FILTERED list, so arrowing after typing moves through what is on screen
 * rather than through the original positions — the bug that version gets wrong is arrowing into
 * an item that is not rendered.
 *
 * ⚠️ **The highlighted row is `aria-activedescendant`, not focus.** Moving real focus per
 * keystroke makes a screen reader re-announce the whole menu each time; the ARIA menu pattern
 * keeps focus on the container and points at the active row.
 */
function DropdownMenu({ items, children, side = 'bottom', align = 'start', width = 224, searchPlaceholder, onQueryChange, emptyLabel, label, className, }) {
    const [open, setOpen] = (0, react_1.useState)(false);
    const [query, setQuery] = (0, react_1.useState)('');
    const [active, setActive] = (0, react_1.useState)(-1);
    /* 29 Oct 2026 · the row being edited, by its label — `MenuItemEdit`. A label rather than an index
       or the object: the caller rebuilds `items` on every render, and a row's position can move. */
    const [editing, setEditing] = (0, react_1.useState)(null);
    const [draft, setDraft] = (0, react_1.useState)('');
    const scope = (0, react_1.useId)();
    const dismiss = () => {
        setOpen(false);
        setActive(-1);
        setQuery('');
        setEditing(null);
        onQueryChange?.('');
    };
    const { surface, trigger } = (0, overlay_1.useDismiss)(open, dismiss);
    const { rect, measure } = (0, overlay_1.useAnchorRect)(open, trigger);
    const searchable = searchPlaceholder !== undefined;
    const visible = query === '' || onQueryChange !== undefined
        ? items
        : items.filter((item) => item.type === undefined &&
            `${item.label ?? ''} ${item.hint ?? ''} ${item.description ?? ''}`
                .toLowerCase()
                .includes(query.toLowerCase()));
    const reachable = selectableIndices(visible);
    const choose = (item) => {
        if (item.disabled === true)
            return;
        /* ⚠️ An editable row opens its field and keeps the menu open — choosing it is not the action. */
        if (item.edit !== undefined) {
            setEditing(item.label ?? '');
            setDraft(item.edit.value);
            return;
        }
        /* A toggle row flips in place — the pill moving is the answer, and closing would hide it. */
        if (item.toggle !== true)
            dismiss();
        item.onSelect?.();
    };
    const save = (edit) => {
        const value = draft.trim();
        if (value === '')
            return;
        dismiss();
        if (value !== edit.value)
            edit.onSave(value);
    };
    (0, react_1.useEffect)(() => {
        if (!open)
            return;
        const onKeyDown = (event) => {
            /* ⚠️ While a row is a text field its keys are the field's: Enter saves it, the arrows move the
               caret, Space types a space. This listener runs first (capture), so it has to step aside. */
            if (editing !== null)
                return;
            if (reachable.length === 0)
                return;
            const at = reachable.indexOf(active);
            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    setActive(reachable[Math.min(reachable.length - 1, at + 1)] ?? reachable[0]);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    setActive(at <= 0 ? reachable[reachable.length - 1] : reachable[at - 1]);
                    break;
                case 'Home':
                    event.preventDefault();
                    setActive(reachable[0]);
                    break;
                case 'End':
                    event.preventDefault();
                    setActive(reachable[reachable.length - 1]);
                    break;
                case 'Enter':
                case ' ': {
                    const item = visible[active];
                    if (item !== undefined) {
                        event.preventDefault();
                        choose(item);
                    }
                    break;
                }
                default:
                    break;
            }
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
        };
        /* `visible` and `reachable` are derived from items+query, both in the dep list. */
    }, [open, active, items, query, editing]);
    /* ⚠️ Reset the highlight when the filter changes, and reset it to NOTHING rather than to the
     * first row. Highlighting row 0 on every keystroke means Enter fires whichever item happens
     * to be first after the next character — a menu that acts on a half-typed query. */
    (0, react_1.useEffect)(() => {
        setActive(-1);
    }, [query]);
    const menu = open && rect !== null
        ? (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsxs)("div", { ref: surface, role: "menu", "aria-label": label, "aria-orientation": "vertical", "aria-activedescendant": active >= 0 ? `${scope}-item-${String(active)}` : undefined, tabIndex: -1, style: (0, overlay_1.anchorTo)(rect, { side, align, width }), className: "pop-in bg-popover text-popover-foreground shadow-pop flex flex-col overflow-hidden rounded-md border p-1", children: [searchable ? ((0, jsx_runtime_1.jsxs)("div", { className: "mb-1 flex items-center gap-1.5 border-b px-2 pb-1.5", children: [(0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "search", size: 13, className: "text-muted-foreground" }), (0, jsx_runtime_1.jsx)("input", { 
                            /* ⚠️ `autoFocus` deliberately: the filter is the whole reason this menu
                               has a search box, and focusing anything else puts it a Tab away. No
                               `jsx-a11y` plugin is in this config to exempt. */
                            autoFocus: true, value: query, placeholder: searchPlaceholder, "aria-label": searchPlaceholder, onChange: (event) => {
                                setQuery(event.target.value);
                                onQueryChange?.(event.target.value);
                            }, className: "placeholder:text-muted-foreground h-7 w-full bg-transparent text-[12.5px] outline-none" })] })) : null, (0, jsx_runtime_1.jsx)("div", { className: (0, cn_1.cn)('-mx-1 overflow-x-hidden overflow-y-auto px-1', searchable ? 'max-h-[280px]' : 'min-h-0 flex-1'), children: visible.length === 0 ? ((0, jsx_runtime_1.jsx)("div", { className: "text-muted-foreground px-2 py-3 text-center text-[12px]", children: emptyLabel })) : (visible.map((item, index) => {
                        if (item.type === 'separator') {
                            return ((0, jsx_runtime_1.jsx)("div", { role: "separator", className: "bg-border -mx-1 my-1 h-px" }, `separator-${String(index)}`));
                        }
                        if (item.type === 'label') {
                            return ((0, jsx_runtime_1.jsx)("div", { className: "text-muted-foreground px-2 py-1.5 text-[11px] font-medium tracking-wide uppercase", children: item.label }, `label-${String(index)}-${item.label ?? ''}`));
                        }
                        const edit = item.edit;
                        if (edit !== undefined && editing !== null && editing === (item.label ?? '')) {
                            return ((0, jsx_runtime_1.jsxs)("div", { className: "flex items-center gap-1 px-1 py-0.5", "data-menu-edit": "", children: [(0, jsx_runtime_1.jsx)(field_1.TextInput
                                    /* ⚠️ Focused AND selected from a ref, not `autoFocus` + `onFocus`: React's
                                       `autoFocus` focuses during commit, before the focus listener is live, so
                                       the `select()` never ran and typing appended to the old name instead of
                                       replacing it. Measured in Chrome. Guarded on focus, so a re-render while
                                       somebody types does not select their draft again. */
                                    , { 
                                        /* ⚠️ Focused AND selected from a ref, not `autoFocus` + `onFocus`: React's
                                           `autoFocus` focuses during commit, before the focus listener is live, so
                                           the `select()` never ran and typing appended to the old name instead of
                                           replacing it. Measured in Chrome. Guarded on focus, so a re-render while
                                           somebody types does not select their draft again. */
                                        ref: (element) => {
                                            if (element !== null && document.activeElement !== element) {
                                                element.focus();
                                                element.select();
                                            }
                                        }, value: draft, maxLength: edit.maxLength, "aria-label": edit.fieldLabel, onChange: (event) => {
                                            setDraft(event.target.value);
                                        }, onKeyDown: (event) => {
                                            if (event.key === 'Enter') {
                                                event.preventDefault();
                                                save(edit);
                                            }
                                        }, className: "h-7 text-[13px]" }), (0, jsx_runtime_1.jsx)(button_1.Button, { variant: "ghost", size: "icon-sm", "aria-label": edit.saveLabel, title: edit.saveLabel, disabled: draft.trim() === '', onClick: (event) => {
                                            /* The portal's events still bubble through the React tree — see the
                                               note on the rows below. */
                                            event.stopPropagation();
                                            save(edit);
                                        }, children: (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "check", size: 14 }) })] }, `edit-${String(index)}`));
                        }
                        const hot = index === active;
                        const row = ((0, jsx_runtime_1.jsxs)("div", { id: `${scope}-item-${String(index)}`, role: item.checked === undefined ? 'menuitem' : 'menuitemcheckbox', "data-tour": item.tourId, "aria-checked": item.checked, "aria-disabled": item.disabled, "data-highlighted": hot ? '' : undefined, tabIndex: -1, onMouseEnter: () => {
                                if (item.disabled !== true)
                                    setActive(index);
                            }, 
                            /**
                             * ⚠⚠️ **`stopPropagation`, AND WITHOUT IT A MENU SELECTION IS ALSO A CLICK ON
                             * WHATEVER SURROUNDS THE TRIGGER.** This list is rendered through
                             * `createPortal` into `document.body`, so it is nowhere near the row in the
                             * DOM — but **React events propagate through the COMPONENT tree, not the DOM
                             * tree**, so the click still reaches the `onClick` of whatever contains the
                             * trigger. The portal buys nothing here.
                             *
                             * The symptom: choosing *Edit* on a project row set the dialog's state and
                             * then the row's own click handler opened the project, unmounting the list
                             * with the dialog on it. Reported as *"it brings me to the project page but
                             * the edit panel doesn't show up"* — both halves of which had happened.
                             *
                             * ⚠️ **Fixed here rather than at the call site**, because every row menu in the
                             * product sits inside a clickable row and each one would otherwise have to
                             * remember. `DataList`'s rows, the timesheet's cells and the project header's
                             * kebab all rely on this.
                             */
                            onClick: (event) => {
                                event.stopPropagation();
                                choose(item);
                            }, className: (0, cn_1.cn)('relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-[13px] outline-none select-none', item.disabled === true && 'opacity-50', item.disabled === true &&
                                item.tooltip === undefined &&
                                'pointer-events-none', hot
                                ? item.destructive === true
                                    ? 'bg-destructive/10 text-destructive'
                                    : 'bg-accent text-accent-foreground'
                                : item.destructive === true && 'text-destructive'), children: [item.dot === undefined ? null : ((0, jsx_runtime_1.jsx)("span", { className: (0, cn_1.cn)('size-2 shrink-0 rounded-full', 
                                    /* On the NAME's line when a description runs under it, as the icon. */
                                    item.description !== undefined && 'mt-[5px] self-start'), 
                                    /* An identity hue is an index into data, so it cannot be a class —
                                       see the `--id-*` note in `base.css`. */
                                    style: { background: item.dot } })), item.icon === undefined ? null : ((0, jsx_runtime_1.jsx)(icon_1.Icon, { name: item.icon, size: 14, className: (0, cn_1.cn)('text-muted-foreground', item.description !== undefined && 'mt-0.5 self-start') })), item.description === undefined ? (item.warning === undefined ? ((0, jsx_runtime_1.jsx)("span", { className: "flex-1 truncate", children: item.label })) : ((0, jsx_runtime_1.jsxs)("span", { className: "flex min-w-0 flex-1 items-center gap-1.5", children: [(0, jsx_runtime_1.jsx)("span", { className: "truncate", children: item.label }), (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "triangle-alert", size: 13, className: "text-status-warn-text shrink-0" }), (0, jsx_runtime_1.jsx)("span", { className: "sr-only", children: item.warning })] }))) : ((0, jsx_runtime_1.jsxs)("span", { className: "flex min-w-0 flex-1 flex-col gap-0.5", children: [(0, jsx_runtime_1.jsx)("span", { className: "truncate font-medium", children: item.label }), (0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground text-[11.5px] leading-snug", children: item.description })] })), item.hint === undefined ? null : ((0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground shrink-0 text-[11px]", children: item.hint })), item.toggle === true && item.checked !== undefined ? ((0, jsx_runtime_1.jsx)("span", { "aria-hidden": true, className: (0, cn_1.cn)('inline-flex h-[18px] w-8 shrink-0 items-center rounded-full transition-colors duration-150', item.checked ? 'bg-primary' : 'bg-input'), children: (0, jsx_runtime_1.jsx)("span", { className: (0, cn_1.cn)('bg-background block size-3.5 rounded-full shadow-sm transition-transform duration-150', item.checked ? 'translate-x-[15px]' : 'translate-x-[2px]') }) })) : item.checked === true ? ((0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "check", size: 14 })) : null, item.tooltip === undefined ? null : ((0, jsx_runtime_1.jsx)("span", { className: "sr-only", children: item.tooltip }))] }, `${item.label ?? ''}-${String(index)}`));
                        return item.tooltip === undefined ? (row) : ((0, jsx_runtime_1.jsx)(tooltip_1.Tooltip, { asChild: true, content: item.tooltip, children: row }, `${item.label ?? ''}-${String(index)}`));
                    })) })] }), document.body)
        : null;
    /**
     * ⚠️ **The portal is a SIBLING of the trigger, not its child, and this is a defect fix.**
     *
     * `createPortal` moves the DOM node but **not** the React tree position: a synthetic event
     * from inside the portal still bubbles through the React parents. With the menu rendered
     * inside the trigger `<div>` — which is what the design system's implementation does — every
     * click in the menu ALSO runs the trigger's toggle.
     *
     * ⚠️ **On a menu item that is invisible**, which is why it survives: the item's own handler
     * has already closed the menu, so the stray toggle closes an already-closed menu and nothing
     * looks wrong. The defect only shows on a click that is not meant to close anything — the
     * search field, a section label, the scrollbar. Clicking the search field closed the menu, so
     * the first keystroke went nowhere and the filter appeared not to work at all. That is how
     * this was found, and `menu.test.tsx` asserts on the search field for the same reason.
     */
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("div", { ref: trigger, className: (0, cn_1.cn)('relative', className), "aria-haspopup": "menu", "aria-expanded": open, onClick: () => {
                    if (open) {
                        dismiss();
                    }
                    else {
                        measure();
                        setOpen(true);
                    }
                }, onKeyDown: (event) => {
                    if (open)
                        return;
                    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        measure();
                        setOpen(true);
                        /* Opening by keyboard lands on the first item; opening by pointer lands on nothing,
                         * because a pointer user has not asked to move a selection yet. */
                        setActive(selectableIndices(items)[0] ?? -1);
                    }
                }, children: children }), menu] }));
}
//# sourceMappingURL=dropdown-menu.js.map